import NetworkBehavior from "../lib/NetworkBehavior";
import { Time } from "../lib/Time";
import Transform from "../lib/Transform";
import { Physics } from "../math/Physics";
import Vector2 from "../math/Vector2";
import Color from "../util/Color";
import Asteroid from "./Asteroid";
import Player from "./Player";
import Trail from "./Trail";

enum State {
    Searching,
    LockedOn
}

export default class HomingRocketBehavior extends NetworkBehavior {
    team: "red"|"blue"|undefined;
    ownerName = "";
    state: State = State.Searching;
    velocity = Vector2.zero;
    acceleration = Vector2.zero;
    accelerationForce = 300;
    friction = 0;
    lifetime = 10;
    explosionSize = 64;
    damage = 100;
    scanRange = 512;
    scanFOV = 90;
    target: Player|undefined;
    trail: Trail|undefined;

    Init(): void {
        this.trail = this.gameObject.AddScript(Trail);
    }

    Die(playersInRange: Player[]) {
        if (this.isOwner) {
            this.engine.createExplosionForAll({
                position: this.transform.position,
                size: this.explosionSize,
                color: new Color(255, 255, 80)
            });

            for (const player of playersInRange) {
                this.engine.sendDamagePacket(player, this.damage);
            }
        }

        this.engine.RemoveGameObject(this.gameObject);
    }

    SearchingUpdate() {
        const sensorLeftT = this.transform.rotation.angle - this.scanFOV/2*Math.PI/180;
        const sensorRightT = this.transform.rotation.angle + this.scanFOV/2*Math.PI/180;

        const validPlayers = this.engine.GetGameObjectsBehaviors<Player>(Player).filter(
            v => !v.isDead && v.team && v.team !== this.team
        );

        for (let i = sensorLeftT; i < sensorRightT; i += (sensorRightT-sensorLeftT)/1024) {
            const vec = Vector2.fromRadians(i);
            const end = this.transform.position.add(vec.scale(this.scanRange));

            for (const player of validPlayers) {
                if (Physics.CircleLineCollision({
                    position: player.transform.position,
                    radius: player.size
                }, {
                    start: this.transform.position,
                    end
                })) {
                    this.target = player;
                    this.state = State.LockedOn;
                    return;
                }
            }

            
        }
    }

    LockedOnUpdate() {
        if (!this.target) return;

        if (this.target.isDead) {
            this.target = undefined;
            this.state = State.Searching;
            return;
        }

        this.transform.rotation = this.velocity.normalized;
        this.acceleration = this.target.transform.position.sub(this.transform.position).normalized.scale(this.accelerationForce);
    }

    Update(): void {
        this.lifetime -= Time.deltaTime;

        

        switch (this.state) {
            case State.Searching:
                this.SearchingUpdate();
                break;
            case State.LockedOn:
                this.LockedOnUpdate();
                break;
        
            default:
                break;
        }

        const validPlayers = this.engine.GetGameObjectsBehaviors<Player>(Player).filter(
            v => !v.isDead && v.team && v.team !== this.team && Vector2.distance(v.transform.position, this.transform.position) <= this.explosionSize + v.size
        );

        for (const player of validPlayers) {
            if (Physics.CircleCircleCollision({
                position: player.transform.position,
                radius: player.size
            }, {
                position: this.transform.position,
                radius: 4
            }) || Vector2.distance(player.transform.position, this.transform.position) <= this.explosionSize/2) {
                this.Die(validPlayers);
                return;
            }
        }

        for (const asteroid of this.engine.GetGameObjectsBehaviors<Asteroid>(Asteroid)) {
            if (Physics.CircleShapeCollision({
                position: this.transform.position,
                radius: 4
            }, asteroid.pointVectors.map(v => asteroid.transform.position.add(v)))) {
                this.Die(validPlayers);
                return;
            }
        }

        if (this.lifetime <= 0) {
            this.Die(validPlayers);
            return;
        }

        const friction = this.state === State.LockedOn ? 0.5 : 0;

        this.velocity = this.velocity.add(this.acceleration.scale(Time.deltaTime));
        this.velocity = this.velocity.sub(this.velocity.scale(friction * Time.deltaTime));
        this.transform.position = this.transform.position.add(this.velocity.scale(Time.deltaTime));
    }

    Draw(): void {
        this.ctx.lineWidth = 2;
        this.ctx.strokeStyle = '#ffff55';
        this.engine.Line(this.transform.position.add(this.transform.forward.scale(4)), this.transform.position.add(this.transform.back.scale(4)));

        this.ctx.fillStyle = '#ffff5555';
        this.engine.FillRect(this.transform.position.add(new Vector2(-25, 12)), new Vector2(50, 4));
        this.ctx.fillStyle = '#ffff55';
        this.engine.FillRect(this.transform.position.add(new Vector2(-25, 12)), new Vector2(50 * (this.lifetime/10), 4));
    }

    DrawGizmos(): void {
        if (this.state === State.Searching) {
            const sensorLeftT = this.transform.rotation.angle - this.scanFOV/2*Math.PI/180;
            const sensorRightT = this.transform.rotation.angle + this.scanFOV/2*Math.PI/180;
            this.ctx.strokeStyle = '#ffff5510';
            this.ctx.lineWidth = 1;
            for (let i = sensorLeftT; i < sensorRightT; i += (sensorRightT-sensorLeftT)/1024) {
                const vec = Vector2.fromRadians(i);
                this.engine.Line(this.transform.position, this.transform.position.add(vec.scale(this.scanRange)));
            }
        }
    }
}